import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import Navbar from '../components/Navbar';
import TryoutSchedule from '../components/TryoutSchedule';
import WaiverForm from '../components/parent/WaiverForm';
import { Plus, Edit, ChevronRight, AlertTriangle, CheckCircle, Calendar, Clock, MapPin, Info } from 'lucide-react';
import { formatDateString, parseDateString } from '../lib/utils';

const toTitleCase = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';

export function ParentDashboard() {
  const { profile, user } = useAuth();
  const [players, setPlayers] = useState<any[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [isAddingPlayer, setIsAddingPlayer] = useState(false);
  const [isEditingPlayer, setIsEditingPlayer] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingPlayerId, setEditingPlayerId] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [sex, setSex] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [positions, setPositions] = useState<string[]>([]);
  const [competitiveness, setCompetitiveness] = useState<string[]>([]);
  const [profileNotes, setProfileNotes] = useState('');
  const [shirtSize, setShirtSize] = useState('');
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [invitePhoneNumber, setInvitePhoneNumber] = useState('');
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [showWaiverForm, setShowWaiverForm] = useState(false);
  const [newPlayerId, setNewPlayerId] = useState<string | null>(null);
  const [currentWaiverPlayerId, setCurrentWaiverPlayerId] = useState<string | null>(null);
  const [playersWithWaiverStatus, setPlayersWithWaiverStatus] = useState<{[key: string]: boolean}>({});
  const [loadingWaiverStatus, setLoadingWaiverStatus] = useState(true);
  const [assignedParents, setAssignedParents] = useState<any[]>([]);
  const [invitedParents, setInvitedParents] = useState<any[]>([]);
  const [loadingParents, setLoadingParents] = useState(false);
  const [showTryoutInfoModal, setShowTryoutInfoModal] = useState(false);
  const [selectedPlayerTryoutInfo, setSelectedPlayerTryoutInfo] = useState<any>(null);
  const [loadingTryoutInfo, setLoadingTryoutInfo] = useState(false);
  const [showCompetitivenessModal, setShowCompetitivenessModal] = useState(false);

  // Function to check if a player has completed all required waivers
  const checkWaiverStatus = async (playerId: string) => {
    // Get the list of mandatory waivers
    const mandatoryWaivers = ['CLUB_LIABILITY_WAIVER', 'COMMUNICABLE_DISEASE_WAIVER', 'SOCIAL_MEDIA_POLICY'];
    
    // Check if there are entries in waiver_log for this player with these waiver types
    const { data, error } = await supabase
      .from('waiver_log')
      .select('waiver_name, result_code')
      .eq('player_id', playerId)
      .in('waiver_name', mandatoryWaivers)
      .eq('result_code', 'agreed');

    if (error) {
      console.error('Error checking waiver status:', error);
      return false;
    }
    
    // If the count of agreed waivers matches the mandatory waivers length, all are completed
    return data && data.length >= mandatoryWaivers.length;
  };

  useEffect(() => {
    const fetchPlayers = async () => {
      setLoadingPlayers(true);
      setLoadingWaiverStatus(true);
      
      if (user) {
        const { data, error } = await supabase
          .from('player_parent_relationships')
          .select('player:player_id(id, first_name, last_name, sex, birthdate, shirt_size, profile, age_group_id)')
          .eq('parent_id', user.id);

        if (error) {
          console.error('Error fetching players:', error);
          setMessage('Error fetching players.');
        } else {
          // Extract player data from the relationships
          const playersData = data.map((rel: any) => rel.player);
          setPlayers(playersData || []);
          
          // Check waiver status for each player
          const waiverStatus: {[key: string]: boolean} = {};
          for (const player of playersData) {
            waiverStatus[player.id] = await checkWaiverStatus(player.id);
          }
          setPlayersWithWaiverStatus(waiverStatus);
          setLoadingWaiverStatus(false);
        }
      }
      setLoadingPlayers(false);
    };

    fetchPlayers();
  }, [user, isAddingPlayer, isEditingPlayer, showWaiverForm]);

  const handleAddPlayerClick = async () => {
    // Check if the user's profile is complete
    if (!profile?.first_name) {
      // User's profile is incomplete, show alert and redirect
      alert('You need to complete your parent profile before registering a player.');
      window.location.href = '/profile';
      return;
    }
    
    // Continue with adding player since profile is complete
    setIsAddingPlayer(true);
    setIsEditingPlayer(false);
    setEditingPlayerId(null);
    setFirstName('');
    setLastName('');
    setSex('');
    setBirthdate('');
    setShirtSize('');
    setPositions([]);
    setCompetitiveness([]);
    setProfileNotes('');
    setMessage('');
    setFormError('');
  };

  const handleEditPlayerClick = (player: any) => {
    setIsEditingPlayer(true);
    setIsAddingPlayer(false);
    setEditingPlayerId(player.id);
    setFirstName(player.first_name);
    setLastName(player.last_name);
    setSex(player.sex);
    setBirthdate(player.birthdate);
    setShirtSize(player.shirt_size || '');
    if (player.profile) {
      setPositions(player.profile.positions || []);
      setCompetitiveness(player.profile.competitiveness || []);
      setProfileNotes(player.profile.notes || '');
    } else {
      setPositions([]);
      setCompetitiveness([]);
      setProfileNotes('');
    }
    setMessage('');
    setFormError('');
    
    // Fetch player's parents when editing
    fetchPlayerParents(player.id);
  };
  
  // Function to fetch assigned parents and invited parents for a player
  const fetchPlayerParents = async (playerId: string) => {
    setLoadingParents(true);
    try {
      // Fetch all parent_id from player_parent_relationships for the player
      const { data: parentData, error: parentError } = await supabase
        .from('player_parent_relationships')
        .select('parent_id')
        .eq('player_id', playerId);
      
      if (parentError) {
        console.error('Error fetching parents:', parentError);
      }
      
      // Fetch assigned profiles where the id is within the parents array
      const { data: assignedData, error: assignedError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, phone_number, email')
        .in('id', (parentData?.map((rel: any) => rel.parent_id) ?? []));
      
      if (assignedError) {
        console.error('Error fetching assigned parents:', assignedError);
      } else {
        // Add status property to indicate these are active parents
        const activeParents = assignedData.map((parent: any) => ({
          ...parent,
          status: 'active',
          isPending: false
        })) || [];
        setAssignedParents(activeParents);
      }
      
      // Fetch pending invites for this player
      const { data: inviteData, error: inviteError } = await supabase
        .from('invitations')
        .select('id, email, first_name, last_name, phone_number, status, invitation_players!inner(*)')
        .eq('invitation_players.player_id', playerId)
        .eq('status', 'pending');
        
      if (inviteError) {
        console.error('Error fetching invited parents:', inviteError);
      } else {
        // Format invited parents to match the structure of active parents
        const pendingParents = inviteData.map((invite: any) => ({
          id: invite.id,
          email: invite.email,
          first_name: invite.first_name,
          last_name: invite.last_name,
          phone_number: invite.phone_number,
          status: 'pending',
          isPending: true
        })) || [];
        setInvitedParents(pendingParents);
      }
    } catch (error) {
      console.error('Error fetching parents:', error);
    } finally {
      setLoadingParents(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingPlayer(false);
    setIsAddingPlayer(false);
    setEditingPlayerId(null);
    setFirstName('');
    setLastName('');
    setSex('');
    setBirthdate('');
    setPositions([]);
    setCompetitiveness([]);
    setProfileNotes('');
    setShirtSize('');
    setMessage('');
    setFormError('');
    setAssignedParents([]);
    setInvitedParents([]);
  };

  const handleAddPlayer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setFormError('');
    if (!firstName || !lastName || !sex || !birthdate) {
      setFormError('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      // Use the Supabase function to handle the entire process in one transaction
      // This function has SECURITY DEFINER privileges and can bypass RLS
      const { data: playerData, error } = await supabase.rpc('add_player_with_relationship', {
        p_first_name: firstName,
        p_last_name: lastName,
        p_sex: sex,
        p_birthdate: birthdate,
        p_shirt_size: shirtSize,
        p_profile: {
          positions,
          competitiveness,
          notes: profileNotes
        }
      });

      if (error) {
        throw error;
      }

      // Success! Update the UI
      setMessage('Player added successfully!');
      setPlayers([...players, playerData]);

      // Meta Pixel: track successful player registration
      if (typeof (window as any).fbq === 'function') {
        (window as any).fbq('track', 'CompleteRegistration');
      }
      
      // Show waiver form for the new player
      setNewPlayerId(playerData.id);
      setShowWaiverForm(true);
      
      // Initialize waiver status for the new player
      setPlayersWithWaiverStatus({
        ...playersWithWaiverStatus,
        [playerData.id]: false
      });

      // Reset form fields
      setFirstName('');
      setLastName('');
      setSex('');
      setBirthdate('');
      setPositions([]);
      setCompetitiveness([]);
      setProfileNotes('');
      setShirtSize('');
    } catch (error: any) {
      console.error('Error adding player:', error);
      setMessage('Failed to add player. Please try again.');
      setFormError(error.message);
    } finally {
      setIsAddingPlayer(false);
      setIsSubmitting(false);
    }
  };

  const handleUpdatePlayer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setFormError('');
    if (!firstName || !lastName || !sex || !birthdate) {
      setFormError('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    const { error } = await supabase
      .from('players')
      .update({
        first_name: firstName,
        last_name: lastName,
        sex,
        birthdate,
        shirt_size: shirtSize,
        profile: {
          positions,
          competitiveness,
          notes: profileNotes
        }
      })
      .eq('id', editingPlayerId);

    if (error) {
      console.error('Error updating player:', error);
      setMessage('Failed to update player. Please try again.');
      setFormError(error.message);
    } else {
      setMessage('Player updated successfully!');
      setEditingPlayerId(null);
      setFirstName('');
      setLastName('');
      setSex('');
      setBirthdate('');
      setPositions([]);
      setCompetitiveness([]);
      setProfileNotes('');
      setShirtSize('');
    }
    setIsEditingPlayer(false);
    setIsSubmitting(false);
  };

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedPlayers.length === 0) {
      setMessage('Please select at least one player.');
      return;
    }
    try {
      const { error } = await supabase.rpc('invite_parent', {
        p_email: inviteEmail,
        p_player_ids: selectedPlayers,
        p_first_name: inviteFirstName,
        p_last_name: inviteLastName,
        p_phone_number: invitePhoneNumber,
      });
      if (error) {
        throw error;
      }
      setMessage('Invite sent successfully.');
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
      setInvitePhoneNumber('');
      setSelectedPlayers([]);
      setIsInviting(false);
    } catch (err: any) {
      console.error('Error inviting parent:', err);
      setMessage('Failed to send invite: ' + err.message);
    }
  };

  // Function to format time strings
  const formatTimeString = (timeString: string): string => {
    try {
      // Parse the time string (expected format: 'HH:MM:SS')
      const [hours, minutes] = timeString.split(':').map(Number);
      
      // Create a date object to use the built-in formatting
      const date = new Date();
      date.setHours(hours, minutes, 0);
      
      // Format the time
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString; // Return the original string if there's an error
    }
  };

  // Function to fetch and process tryout information for a player
  const handleShowTryoutInfo = async (player: any) => {
    setShowTryoutInfoModal(true);
    setLoadingTryoutInfo(true);
    setSelectedPlayerTryoutInfo(null);
    
    try {
      if (!player.age_group_id) {
        setSelectedPlayerTryoutInfo({ noSessions: true, ageGroup: { group_name: 'Unknown Age Group' } });
        return;
      }

      // Fetch the age group details
      const { data: ageGroupData, error: ageGroupError } = await supabase
        .from('age_groups')
        .select('*')
        .eq('id', player.age_group_id)
        .single();

      if (ageGroupError || !ageGroupData) {
        console.error('Error fetching age group:', ageGroupError);
        setSelectedPlayerTryoutInfo({ noSessions: true, ageGroup: { group_name: 'Error Loading Group' } });
        return;
      }

      // Fetch evaluation sessions for this age group
      const { data: sessionLinks, error: linkError } = await supabase
        .from('evaluation_session_age_groups')
        .select('evaluation_session_id')
        .eq('age_group_id', player.age_group_id);

      if (linkError || !sessionLinks) {
        console.error('Error fetching session links:', linkError);
        setSelectedPlayerTryoutInfo({ noSessions: true, ageGroup: ageGroupData });
        return;
      }

      if (sessionLinks.length === 0) {
        setSelectedPlayerTryoutInfo({ sessions: [], ageGroup: ageGroupData });
        return;
      }

      // Fetch the actual session details
      const sessionIds = sessionLinks.map(link => link.evaluation_session_id);
      const { data: sessions, error: sessionsError } = await supabase
        .from('evaluation_sessions')
        .select('*')
        .in('id', sessionIds)
        .order('session_date', { ascending: true });

      if (sessionsError || !sessions) {
        console.error('Error fetching sessions:', sessionsError);
        setSelectedPlayerTryoutInfo({ noSessions: true, ageGroup: ageGroupData });
        return;
      }

      // Format the sessions for display
      const formattedSessions = sessions.map(session => {
        // Format date
        const date = parseDateString(session.session_date);
        const formattedDate = date.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });
        
        // Format times
        const startTime = formatTimeString(session.start_time);
        const endTime = formatTimeString(session.end_time);
        const timeRange = `${startTime} - ${endTime}`;
        
        return {
          id: session.id,
          date: formattedDate,
          time: timeRange,
          location: {
            name: session.location_name || 'TBD',
            address: session.location_address || 'Location to be determined'
          },
          notes: 'Please arrive 15 minutes early for check-in. Bring a ball, cleats, shin guards, and water.'
        };
      });

      setSelectedPlayerTryoutInfo({
        sessions: formattedSessions,
        ageGroup: ageGroupData
      });

    } catch (error) {
      console.error('Error fetching tryout info:', error);
      setSelectedPlayerTryoutInfo({ 
        noSessions: true, 
        error: true,
        ageGroup: { group_name: 'Error Loading Placements' } 
      });
    } finally {
      setLoadingTryoutInfo(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-center">
          <img src="/cdo.png" alt="Club Development Organization" className="h-24 object-contain" />
        </div>
      </div>
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 sm:mb-0">Your Players</h2>
            <div className="grid grid-cols-1 gap-3 w-full sm:flex sm:space-x-4 sm:w-auto">
              {!isEditingPlayer && !isAddingPlayer && !isInviting && (
                <>
                <button
                  className="w-full justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => setIsInviting(true)}
                >
                  Add Parent/Guardian
                </button>
              
                <button
                  onClick={handleAddPlayerClick}
                  className="w-full justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Add Player
                </button>
                </>
              )}
            </div>
          </div>

          {message && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative" role="alert">
              <span className="block sm:inline">{message}</span>
              <button 
                onClick={() => setMessage('')} 
                className="absolute top-0 bottom-0 right-0 px-4 py-3"
              >
                <span className="sr-only">Close</span>
                <svg className="fill-current h-6 w-6 text-green-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <title>Close</title>
                  <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
                </svg>
              </button>
            </div>
          )}

          {(!isEditingPlayer && !isAddingPlayer) && (
            loadingPlayers ? (
              <p>Loading players...</p>
            ) : players.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {players.map((player) => (
                  <div
                    key={player.id}
                    className="bg-white p-6 rounded-lg shadow-md border-l-4 border-indigo-500 hover:shadow-xl transition transform hover:-translate-y-1"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-semibold text-gray-800">
                        {player.first_name} {player.last_name}
                      </h3>
                      <div className="flex items-center space-x-2">
                        {!loadingWaiverStatus && playersWithWaiverStatus[player.id] && (
                          <div 
                            className="flex items-center text-green-600 bg-green-50 px-2 py-1 rounded"
                            title="All mandatory waivers completed"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            <span className="text-xs font-medium">Registered</span>
                          </div>
                        )}
                        <ChevronRight className="w-6 h-6 text-gray-400" />
                      </div>
                    </div>
                    <div className="text-gray-600">
                      <p className="text-sm">
                        <span className="font-medium">Date of Birth:</span>{' '}
                        {player.birthdate
                          ? formatDateString(player.birthdate, 'en-US', 'long')
                          : ''}
                      </p>
                      <p className="text-sm">
                        <span className="font-medium">Gender:</span> {toTitleCase(player.sex)}
                      </p>
                    </div>
                    <div className="flex justify-between mt-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditPlayerClick(player);
                        }}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center"
                      >
                        <Edit className="w-4 h-4 mr-1" /> Edit Details
                      </button>
                      <div className="flex space-x-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowTryoutInfo(player);
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                        >
                          <Info className="w-4 h-4 mr-1" /> Placement Info
                        </button>
                        {!loadingWaiverStatus && !playersWithWaiverStatus[player.id] && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentWaiverPlayerId(player.id);
                              setShowWaiverForm(true);
                            }}
                            className="text-amber-600 hover:text-amber-800 text-sm font-medium flex items-center"
                          >
                            <AlertTriangle className="w-4 h-4 mr-1" /> Complete Waivers
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p>No players added yet.</p>
            )
          )}

          {/* Notice about adding other parents/guardians */}
          {(!isEditingPlayer && !isAddingPlayer && players.length > 0) && (
            <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    After you have registered your players, make sure to add other parents/guardians that would like to be contacted for team placements after the evaluations are complete.{' '}
                    <button
                      onClick={() => setIsInviting(true)}
                      className="font-medium text-blue-700 underline hover:text-blue-600"
                    >
                      Add Parents/Guardians
                    </button>
                  </p>
                </div>
              </div>
            </div>
          )}

          {(isAddingPlayer || isEditingPlayer) && (
            <div className="mt-8 bg-white shadow rounded-md p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                {isAddingPlayer ? (
                  <>
                    <Plus className="h-5 w-5 mr-2 text-indigo-600" />
                    Add New Player
                  </>
                ) : (
                  <>
                    <Edit className="h-5 w-5 mr-2 text-indigo-600" />
                    Edit Player
                  </>
                )}
              </h3>
              <form onSubmit={isAddingPlayer ? handleAddPlayer : handleUpdatePlayer} className="space-y-6">
                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="sex"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Gender
                  </label>
                  <select
                    id="sex"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    value={sex}
                    onChange={(e) => setSex(e.target.value)}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="birthdate"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    id="birthdate"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    value={birthdate}
                    onChange={(e) => setBirthdate(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="shirtSize"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Shirt Size
                  </label>
                  <select
                    id="shirtSize"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    value={shirtSize}
                    onChange={(e) => setShirtSize(e.target.value)}
                    required
                  >
                    <option value="">Select Shirt Size</option>
                    <optgroup label="Youth Sizes">
                      <option value="Youth XS">Youth XS</option>
                      <option value="Youth S">Youth S</option>
                      <option value="Youth M">Youth M</option>
                      <option value="Youth L">Youth L</option>
                      <option value="Youth XL">Youth XL</option>
                    </optgroup>
                    <optgroup label="Adult Sizes">
                      <option value="Adult S">Adult S</option>
                      <option value="Adult M">Adult M</option>
                      <option value="Adult L">Adult L</option>
                      <option value="Adult XL">Adult XL</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Positions (Select all that apply)
                  </label>
                  <div className="grid grid-cols-2 gap-2 w-full">
                    {['Goalkeeper', 'Defender', 'Midfielder', 'Forward'].map((pos) => (
                      <label key={pos} className="flex items-center break-words whitespace-normal">
                        <input
                          type="checkbox"
                          value={pos}
                          checked={positions.includes(pos)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPositions([...positions, pos]);
                            } else {
                              setPositions(positions.filter((p) => p !== pos));
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="ml-2">{pos}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Desired Competitiveness Levels (Select all that apply)<br />
                    <button 
                      type="button"
                      className="text-indigo-600 hover:text-indigo-800 text-sm font-medium underline focus:outline-none"
                      onClick={() => setShowCompetitivenessModal(true)}
                    >
                      Click here for a description of each level.
                    </button>
                  </label>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {['Premier', 'Elite', 'Alliance'].map((level) => (
                      <label key={level} className="flex items-center">
                        <input
                          type="checkbox"
                          value={level}
                          checked={competitiveness.includes(level)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCompetitiveness([...competitiveness, level]);
                            } else {
                              setCompetitiveness(competitiveness.filter((l) => l !== level));
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="ml-2">{level}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="profileNotes"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Additional Notes for Coaches
                  </label>
                  <textarea
                    id="profileNotes"
                    value={profileNotes}
                    onChange={(e) => setProfileNotes(e.target.value)}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    rows={3}
                  ></textarea>
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Saving…' : isAddingPlayer ? 'Add Player' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                </div>
                {formError && <p className="text-red-500 text-sm">{formError}</p>}
                {message && <p className="text-green-500 text-sm">{message}</p>}
              </form>
              
              {/* Only show parents section when editing a player, not when adding */}
              {isEditingPlayer && (
                <div className="mt-8 border-t pt-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Player's Parents/Guardians</h4>
                  
                  <div className="space-y-6">
                    {/* Combined Parents and Invitations Section */}
                    <div>
                      {loadingParents ? (
                        <p className="text-sm text-gray-500">Loading parents information...</p>
                      ) : assignedParents.length > 0 || invitedParents.length > 0 ? (
                        <div className="bg-gray-50 rounded-md p-4">
                          <div className="grid grid-cols-1 gap-4">
                            {/* Active Parents */}
                            {assignedParents.map((parent) => (
                              <div key={`active-${parent.id}`} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                  <div>
                                    <span className="text-sm font-medium text-gray-600">Name:</span>
                                    <span className="ml-2 text-sm text-gray-900">
                                      {parent.first_name || ''} {parent.last_name || ''}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-600">Email:</span>
                                    <span className="ml-2 text-sm text-gray-900">{parent.email || 'No email'}</span>
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-600">Phone:</span>
                                    <span className="ml-2 text-sm text-gray-900">
                                      {parent.phone_number || 'No phone number'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                            
                            {/* Pending Invitations */}
                            {invitedParents.map((invite) => (
                              <div key={`invite-${invite.id}`} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                  <div>
                                    <span className="text-sm font-medium text-gray-600">Name:</span>
                                    <span className="ml-2 text-sm text-gray-900">
                                      {invite.first_name || ''} {invite.last_name || ''}
                                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                        Pending
                                      </span>
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-600">Email:</span>
                                    <span className="ml-2 text-sm text-gray-900">{invite.email}</span>
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-600">Phone:</span>
                                    <span className="ml-2 text-sm text-gray-900">
                                      {invite.phone_number || 'No phone number'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No other parents or pending invitations for this player.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <Dialog open={showCompetitivenessModal} onOpenChange={setShowCompetitivenessModal}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Competitiveness Levels Description</DialogTitle>
              </DialogHeader>
              <div className="mt-2 px-2 py-3 max-h-[70vh] overflow-y-auto">
                <h4 className="font-bold mb-1">Premier:</h4>
                <p className="text-sm text-gray-700 mb-4">
                  CDO Premier teams consist of the most talented and driven group of players CDO can assemble in that particular age group, and as such, the aspirations and expectations of that team are of the highest level accordingly. Premier teams seek to compete in the highest level leagues possible and seek out competitive tournaments, as they strive to raise their level of play, knowledge of the game, and bolster any future they wish to pursue in the game be it club, college, or professional. Players on Premier teams can be local or hail from any area should they be committed and dedicated enough to regularly travel for training and matches.
                </p>
                
                <h4 className="font-bold mb-1">Elite:</h4>
                <p className="text-sm text-gray-700 mb-4">
                  Behind our Premier Teams we have our Elite Team program. At CDO we acknowledge that some players develop quicker than others, some need a slower trajectory and some need to have a slightly less intense environment. This is where our Elite Level comes in. Players still get to work with high level coaches, working under the same curriculum and ideals as the Premier Level program as we look to help players progress and move up to the Premier Level when they are ready. Elite team players who are striving to truly challenge themselves are encouraged to accept opportunities to play for the Premier teams when those offers become available in order to gain experience playing at that level.
                </p>
                
                <h4 className="font-bold mb-1">Alliance:</h4>
                <p className="text-sm text-gray-700 mb-4">
                  CDO Alliance teams consist of players with less experience in the game than those on Premier and Elite groups. However, the expectation of Alliance players is that they still remain committed to their team and strive to be the best player they can possibly be. Alliance teams typically play in less tournaments and compete in less challenging divisions of local leagues. Therefore, Alliance team players looking for a greater challenge are encouraged to accept opportunities to play for the Elite teams when those offers become available in order to gain experience playing at that level.
                </p>
              </div>
              <DialogFooter>
                <Button onClick={() => setShowCompetitivenessModal(false)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={isInviting} onOpenChange={setIsInviting}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Parent/Guardian</DialogTitle>
              </DialogHeader>
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-sm text-amber-700">
                  <strong>Note:</strong> It's best to wait until you've added all of your players before inviting other parents/guardians.
                </p>
              </div>
              <form onSubmit={handleInviteSubmit}>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <label htmlFor="inviteFirstName" className="text-sm font-medium">
                      First Name
                    </label>
                    <Input
                      type="text"
                      id="inviteFirstName"
                      value={inviteFirstName}
                      onChange={(e) => setInviteFirstName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="inviteLastName" className="text-sm font-medium">
                      Last Name
                    </label>
                    <Input
                      type="text"
                      id="inviteLastName"
                      value={inviteLastName}
                      onChange={(e) => setInviteLastName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="inviteEmail" className="text-sm font-medium">
                      Email
                    </label>
                    <Input
                      type="email"
                      id="inviteEmail"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="invitePhoneNumber" className="text-sm font-medium">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      id="invitePhoneNumber"
                      value={invitePhoneNumber}
                      onChange={(e) => setInvitePhoneNumber(e.target.value)}
                      placeholder="(555) 555-5555"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Select Players</label>
                    <div className="space-y-2">
                      {players.map((player) => (
                        <div key={player.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`player-${player.id}`}
                            checked={selectedPlayers.includes(player.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedPlayers([...selectedPlayers, player.id]);
                              } else {
                                setSelectedPlayers(selectedPlayers.filter((id) => id !== player.id));
                              }
                            }}
                          />
                          <label htmlFor={`player-${player.id}`} className="text-sm">
                            {player.first_name} {player.last_name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={() => setIsInviting(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="indigo"
                  >
                    Invite
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Tryout Schedule Section - Only show when not editing or adding a player */}
          {!isEditingPlayer && !isAddingPlayer && <TryoutSchedule />}
          
          {/* Waiver Form Dialog */}
          {showWaiverForm && (currentWaiverPlayerId || newPlayerId) && (
            <WaiverForm
              isOpen={showWaiverForm}
              onClose={() => {
                setShowWaiverForm(false);
                setCurrentWaiverPlayerId(null);
              }}
              playerId={currentWaiverPlayerId || newPlayerId || ''}
              onComplete={() => {
                setMessage('Registration completed successfully! No additional action required.');
                if (newPlayerId) {
                  setNewPlayerId(null);
                }
                // Update the waiver status after completion
                if (currentWaiverPlayerId) {
                  setPlayersWithWaiverStatus({
                    ...playersWithWaiverStatus,
                    [currentWaiverPlayerId]: true
                  });
                }
              }}
            />
          )}
          
          {/* Tryout Info Modal */}
          <Dialog open={showTryoutInfoModal} onOpenChange={setShowTryoutInfoModal}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {selectedPlayerTryoutInfo?.ageGroup?.group_name || 'Age Group'} Placement Information
                </DialogTitle>
              </DialogHeader>
              
              {loadingTryoutInfo ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
                </div>
              ) : (
                <div className="py-4">
                  {selectedPlayerTryoutInfo?.noSessions ? (
                    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
                      <p className="text-amber-700">No placement sessions have been scheduled for this age group yet.</p>
                    </div>
                  ) : selectedPlayerTryoutInfo?.sessions?.length === 0 ? (
                    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
                      <p className="text-amber-700">No placement sessions have been scheduled for this age group yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {selectedPlayerTryoutInfo?.sessions?.map((session: any, sessionIndex: number) => (
                        <div key={sessionIndex} className="p-4 border border-gray-100 rounded-md bg-gray-50">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Evaluation Session {sessionIndex + 1}</h4>
                          
                          <div className="space-y-3">
                            <div className="flex items-center text-gray-600">
                              <Calendar className="h-5 w-5 mr-2 text-indigo-500" />
                              <span>{session.date}</span>
                            </div>
                            
                            <div className="flex items-center text-gray-600">
                              <Clock className="h-5 w-5 mr-2 text-indigo-500" />
                              <span>{session.time}</span>
                            </div>
                            
                            <div className="flex items-start text-gray-600">
                              <MapPin className="h-5 w-5 mr-2 text-indigo-500 mt-0.5" />
                              <div>
                                <p className="font-medium">{session.location.name}</p>
                                <p className="text-sm">{session.location.address}</p>
                              </div>
                            </div>
                            
                            {session.notes && (
                              <div className="mt-2 text-sm text-gray-600 italic">
                                {session.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <DialogFooter>
                <Button onClick={() => setShowTryoutInfoModal(false)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
}

export default ParentDashboard;